// Operations views (opsview stream, PD-555 – PD-569):
// - PB-19-08 integration health in the operations summary: counts by state only, never names,
//   identifiers or error text (PD-555, PD-556).
// - PB-19-09 configuration promotion: read-only export / validate / dry-run plan (PD-557 – PD-559).
import { z } from 'zod';

// ------------------------------------------------------------------ integration health (PB-19-08)

/** Device connectivity: heartbeat age against the device's stale window; `pending` = not yet paired. */
export const DEVICE_CONNECTIVITY_STATES = ['connected', 'disconnected', 'never_connected', 'pending'] as const;
export type DeviceConnectivityState = (typeof DEVICE_CONNECTIVITY_STATES)[number];
/** Device kinds counted (module 18 devices plus kiosks, PD-040). */
export const MONITORED_DEVICE_KINDS = ['printer', 'scanner', 'reader', 'encoder', 'edge_agent', 'kiosk'] as const;
export type MonitoredDeviceKind = (typeof MONITORED_DEVICE_KINDS)[number];
/** Heartbeat age of paired devices, fixed buckets (PD-556). */
export const HEARTBEAT_AGE_BUCKETS = ['under_1m', '1m_to_5m', '5m_to_15m', '15m_to_1h', 'over_1h', 'never'] as const;
export type HeartbeatAgeBucket = (typeof HEARTBEAT_AGE_BUCKETS)[number];
/** Last business result reported by a paired device (independent of its connectivity). */
export const DEVICE_JOB_OUTCOMES = ['completed', 'failed', 'unknown', 'none'] as const;
export type DeviceJobOutcome = (typeof DEVICE_JOB_OUTCOMES)[number];
export const CONNECTOR_STATUSES = ['draft', 'enabled', 'disabled'] as const;
export const CONNECTOR_TRANSPORT_STATES = ['connected', 'degraded', 'disconnected', 'never_connected'] as const;
export const CONNECTOR_BUSINESS_STATES = ['confirmed', 'attention', 'unknown', 'none'] as const;
/** Edge agents (active) with each condition; one agent can be in several. */
export const EDGE_AGENT_STATES = ['enrolled', 'buffer_dropped', 'commands_unknown', 'commands_pending'] as const;
/** Outcome of the last access-event poll per active access connector (PD-494): unknown is never ok. */
export const ACCESS_EVENT_POLL_OUTCOMES = ['ok', 'unknown', 'failed'] as const;

type Counts<T extends readonly string[]> = Readonly<Record<T[number], number>>;

export interface IntegrationHealthSummary {
  readonly devices: {
    readonly connectivity: Counts<typeof DEVICE_CONNECTIVITY_STATES>;
    readonly byKind: Readonly<Record<MonitoredDeviceKind, Counts<typeof DEVICE_CONNECTIVITY_STATES>>>;
    readonly heartbeatAge: Counts<typeof HEARTBEAT_AGE_BUCKETS>;
    readonly lastJob: Counts<typeof DEVICE_JOB_OUTCOMES>;
  };
  readonly connectors: {
    readonly status: Counts<typeof CONNECTOR_STATUSES>;
    /** Enabled connectors only. */
    readonly transport: Counts<typeof CONNECTOR_TRANSPORT_STATES>;
    readonly business: Counts<typeof CONNECTOR_BUSINESS_STATES>;
  };
  readonly edgeAgents: Counts<typeof EDGE_AGENT_STATES> & { readonly bufferedEvents: number };
  readonly accessEventPoll: Counts<typeof ACCESS_EVENT_POLL_OUTCOMES>;
}

// ------------------------------------------------------------------ configuration promotion (PB-19-09)

/** Environment-specific reference mapping for a dry run (codes, never database ids). */
export const PromotionMapping = z
  .object({
    sites: z.record(z.string().regex(/^[A-Z0-9][A-Z0-9-]{0,31}$/), z.string().regex(/^[A-Z0-9][A-Z0-9-]{0,31}$/)).optional(),
    locations: z.record(z.string().max(100), z.string().max(100)).optional(),
    /** Location refs whose security level may differ in the target: an explicit, reviewed choice. */
    acceptSecurityLevelChange: z.array(z.string().max(100)).max(500).optional(),
  })
  .strict();
export type PromotionMapping = z.infer<typeof PromotionMapping>;

export const PromotionValidateRequest = z
  .object({
    bundle: z.unknown(),
    /** Accept an unsigned bundle for review (dry run only; nothing can be applied from here). */
    allowUnsigned: z.boolean().default(false),
  })
  .strict();
export const PromotionPlanRequest = PromotionValidateRequest.extend({ mapping: PromotionMapping.default({}) }).strict();

export interface PromotionBundleSummary {
  readonly format: string;
  readonly createdAt: string;
  readonly sourceEnvironment: string;
  readonly schemaVersion: string | null;
  readonly organisation: string;
  readonly digest: string;
  readonly signed: boolean;
  readonly keyId: string | null;
  readonly counts: { readonly sites: number; readonly locations: number; readonly restrictedLocations: number; readonly visitorPolicies: number; readonly branding: number; readonly securityPolicy: boolean; readonly skipped: number };
  readonly skipped: ReadonlyArray<{ readonly kind: string; readonly name: string; readonly reason: string }>;
}

export interface PromotionOverviewResponse {
  readonly environment: string;
  readonly schemaVersion: string | null;
  readonly format: string;
  /** Whether this installation can sign exports and verify signed bundles. */
  readonly signing: { readonly configured: boolean; readonly keyId: string | null };
  /** PD-559: applying a bundle is not available here (H-13); it stays manual through the normal screens. */
  readonly apply: 'not_available';
}

export interface PromotionExportResponse {
  readonly bundle: unknown;
  readonly summary: PromotionBundleSummary;
}

export interface PromotionValidateResponse {
  readonly ok: boolean;
  readonly problems: readonly string[];
  readonly unsignedAccepted: boolean;
  readonly summary: PromotionBundleSummary | null;
}

export interface PromotionPlanItem {
  readonly kind: 'site' | 'location' | 'visitor-policy' | 'security-policy' | 'branding';
  readonly ref: string;
  readonly action: 'create' | 'update' | 'unchanged';
  readonly changes: readonly string[];
}

export interface PromotionPlanResponse {
  readonly validation: PromotionValidateResponse;
  /** Null when validation failed: an invalid bundle is never planned. */
  readonly plan: { readonly ok: boolean; readonly items: readonly PromotionPlanItem[]; readonly blockers: readonly string[] } | null;
}
